import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import React, { useEffect, useState } from 'react'
import { createDuplicateEmailErrors, createEmailFormatErrors, createFieldErrors, createRenewalEligibilityErrors, findDuplicateEmails, hasRequiredFields, isValidEmail } from '../helpers/helpers'
import { IAttendee, IEpiProps, IEventRegistrationRefreshModel, IRenewalEligibilityResponse, IUserSelections, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { AttendeeForm } from './attendee-form'
import { ContentWrapper } from './layout/content-wrapper'
import { MainContent } from './layout/main-content'
import { SideBarContent } from './layout/sidebar-content'
import { ProgramSummary } from './program-summary'


interface IContextProps {
  epiProps: IEpiProps
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
  currentStep: string
  userSelections: IUserSelections
  hideAttendeesStep: boolean
}

interface IAttendeeFormData extends IAttendee {
  id: string // Unique ID for tracking forms
  attendeeNumber: number // Track the actual attendee number
  isEligible?: boolean
  eligibilityChecked?: boolean
}

const MAX_ATTENDEES = 10

const checkRenewalEligibility = async (
  email: string,
  specialtyCode: string
): Promise<IRenewalEligibilityResponse> => {
  try {
    const response = await fetch('/internalapi/EventRegistrationRefreshApi/CheckSingleEmailRenewalEligibility', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        specialtyCode: specialtyCode
      })
    })

    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error checking renewal eligibility:', error)
    throw error
  }
}

export const AttendeesStep = () => {
  const { epiProps, eventRegistrationRefreshModel, currentStep, userSelections, hideAttendeesStep }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false)
  
  // Initialize local state with existing attendees or one empty form
  const [localAttendees, setLocalAttendees] = useState<IAttendeeFormData[]>(() => {
    if (userSelections.attendees && userSelections.attendees.length > 0) {
      // If we have existing attendees, convert them to local format
      return userSelections.attendees.map((att, index) => ({
        ...att,
        id: `attendee-${Date.now()}-${index}`,
        attendeeNumber: index + 1
      }))
    } else {
      // Start with one empty form
      return [{
        id: `attendee-${Date.now()}`,
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        attendeeNumber: 1
      }]
    }
  })
  
  // Track the next attendee number to assign
  const [nextAttendeeNumber, setNextAttendeeNumber] = useState(() => {
    return userSelections.attendees && userSelections.attendees.length > 0 
      ? userSelections.attendees.length + 1 
      : 2
  })
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: Partial<IAttendee> }>({})
  const [showValidationMessage, setShowValidationMessage] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [duplicateEmails, setDuplicateEmails] = useState<Set<string>>(new Set())
useEffect(() => {
  // Only fire the event if this is the active step
  if (currentStep === StepConstants.AttendeesStep) {
      EcomDataLayerHub.viewAttendeeForm(userSelections.attendees.length)
  }
}, [currentStep, epiProps.attendeesStepTitle]) // Re-fire if the step or title changes.

  // Effect to sync local state when global state is cleared (e.g., when program changes)
  useEffect(() => {
    // Check if global attendees are empty but local attendees have data
    // This indicates the attendees were cleared (e.g., program changed)
    if (!userSelections.attendees || userSelections.attendees.length === 0) {
      // Only reset if local state has actual data (not just the default empty form)
      const hasActualData = localAttendees.some(att => 
        att.firstName.trim() || att.lastName.trim() || 
        att.email.trim() || att.title.trim()
      )
      
      // Reset if we have actual data or multiple forms
      if (hasActualData || localAttendees.length > 1) {
        // Reset to single empty form
        setLocalAttendees([{
          id: `attendee-${Date.now()}`,
          firstName: '',
          lastName: '',
          email: '',
          title: '',
          attendeeNumber: 1
        }])
        setNextAttendeeNumber(2)
        setFormErrors({})
        setShowValidationMessage(false)
        setValidationMessage('')
        setDuplicateEmails(new Set())
      }
    }
  }, [userSelections.attendees])


  // Function to sync local state with global state
  const syncGlobalState = (attendees: IAttendeeFormData[]) => {
    // Filter out empty forms (where all fields are empty)
    const filledAttendees = attendees.filter(att => 
      att.firstName.trim() || att.lastName.trim() || att.email.trim() || att.title.trim()
    )
    
    // Remove the id and attendeeNumber fields before saving to global state
    const attendeesToSave: IAttendee[] = filledAttendees.map(({ id, attendeeNumber, ...attendee }) => attendee)
    
    // Update global state
    dispatch({
      type: 'updateUserSelections',
      data: {
        ...userSelections,
        attendees: attendeesToSave
      }
    })
  }

  const handleAddAttendee = () => {
    // Add new empty form at the beginning (top) of the array
    EcomDataLayerHub.modifyAttendees("Add");
    const newAttendee: IAttendeeFormData = {
      id: `attendee-${Date.now()}`,
      firstName: '',
      lastName: '',
      email: '',
      title: '',
      attendeeNumber: nextAttendeeNumber
    }
    setLocalAttendees([newAttendee, ...localAttendees])
    setNextAttendeeNumber(nextAttendeeNumber + 1)
    
    // Disable review step breadcrumb when attendee data changes
    dispatch({
      type: 'resetStepsAfter',
      data: StepConstants.AttendeesStep
    })
  }

  const handleAttendeeChange = (index: number, attendee: IAttendee) => {
    const updatedAttendees = [...localAttendees]
    updatedAttendees[index] = { 
      ...attendee, 
      id: updatedAttendees[index].id, 
      attendeeNumber: updatedAttendees[index].attendeeNumber,
      isEligible: attendee.isEligible,
      eligibilityChecked: attendee.eligibilityChecked
    }
    setLocalAttendees(updatedAttendees)
    
    // Disable review step breadcrumb when attendee data changes
    dispatch({
      type: 'resetStepsAfter',
      data: StepConstants.AttendeesStep
    })
  }

  const handleAttendeeBlur = (index: number, attendee: IAttendee) => {
    // Update local state first
    const updatedAttendees = [...localAttendees]
    updatedAttendees[index] = { 
      ...attendee, 
      id: updatedAttendees[index].id, 
      attendeeNumber: updatedAttendees[index].attendeeNumber,
      isEligible: attendee.isEligible,
      eligibilityChecked: attendee.eligibilityChecked
    }
    
    // Sync with global state
    syncGlobalState(updatedAttendees)
  }

  const handleRemoveAttendee = (index: number) => {
     EcomDataLayerHub.modifyAttendees('Remove');
    let updatedAttendees = localAttendees.filter((_, i) => i !== index)
    
    // Renumber all attendees sequentially
    updatedAttendees = updatedAttendees.map((attendee, idx) => ({
      ...attendee,
      attendeeNumber: updatedAttendees.length - idx
    }))
    
    // Update the next attendee number
    setNextAttendeeNumber(updatedAttendees.length + 1)
    
    // If no attendees left, add an empty one
    if (updatedAttendees.length === 0) {
      updatedAttendees = [{
        id: `attendee-${Date.now()}`,
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        attendeeNumber: 1
      }]
      setNextAttendeeNumber(2)
    }
    
    setLocalAttendees(updatedAttendees)
    
    // Sync with global state after removal
    syncGlobalState(updatedAttendees)
    
    // Disable review step breadcrumb when attendee data changes
    dispatch({
      type: 'resetStepsAfter',
      data: StepConstants.AttendeesStep
    })
  }

  const validateRequiredFields = (attendees: IAttendeeFormData[]): { isValid: boolean; errorMessage: string; errors: { [key: string]: Partial<IAttendee> } } => {
    const errors: { [key: string]: Partial<IAttendee> } = {}
    let hasRequiredFieldErrors = false
    
    attendees.forEach(attendee => {
      if (!hasRequiredFields(attendee)) {
        const fieldErrors = createFieldErrors({ id: attendee.id, ...attendee }, epiProps)
        if (Object.keys(fieldErrors).length > 0) {
          errors[attendee.id] = fieldErrors
          hasRequiredFieldErrors = true
        }
      }
    })
    
    return {
      isValid: !hasRequiredFieldErrors,
      errorMessage: hasRequiredFieldErrors ? epiProps.missingRequiredFieldsSummaryMessage : '',
      errors
    }
  }

  const validateEmailFormat = (attendees: IAttendeeFormData[]): { isValid: boolean; errorMessage: string; errors: { [key: string]: Partial<IAttendee> } } => {
    const errors: { [key: string]: Partial<IAttendee> } = {}
    let hasEmailFormatErrors = false
    
    attendees.forEach(attendee => {
      if (attendee.email.trim() && !isValidEmail(attendee.email)) {
        const formatErrors = createEmailFormatErrors({ id: attendee.id, ...attendee }, epiProps)
        if (Object.keys(formatErrors).length > 0) {
          errors[attendee.id] = formatErrors
          hasEmailFormatErrors = true
        }
      }
    })
    
    return {
      isValid: !hasEmailFormatErrors,
      errorMessage: hasEmailFormatErrors ? epiProps.emailsInvalidSummaryMessage : '',
      errors
    }
  }

  const validateDuplicateEmails = (attendees: IAttendeeFormData[]): { isValid: boolean; errorMessage: string; errors: { [key: string]: Partial<IAttendee> } } => {
    const duplicateEmailsArray = findDuplicateEmails(attendees)
    const errors: { [key: string]: Partial<IAttendee> } = {}
    
    if (duplicateEmailsArray.length > 0) {
      // Set the duplicateEmails state for form field highlighting
      setDuplicateEmails(new Set(duplicateEmailsArray))
      
      // Set field-level errors for duplicate emails
      attendees.forEach(attendee => {
        const duplicateErrors = createDuplicateEmailErrors({ id: attendee.id, ...attendee }, duplicateEmailsArray, epiProps)
        if (Object.keys(duplicateErrors).length > 0) {
          errors[attendee.id] = duplicateErrors
        }
      })
      
      return {
        isValid: false,
        errorMessage: epiProps.duplicateEmailSummaryErrorMessage,
        errors
      }
    }
    
    // Clear duplicate emails if none found
    setDuplicateEmails(new Set())
    return {
      isValid: true,
      errorMessage: '',
      errors
    }
  }

  const validateRenewalEligibility = (attendees: IAttendeeFormData[]): { isValid: boolean; errorMessage: string; errors: { [key: string]: Partial<IAttendee> } } => {
    const isRenewalProgram = userSelections.userProgramSelection?.isRenewal === true
    const errors: { [key: string]: Partial<IAttendee> } = {}
    
    if (!isRenewalProgram) {
      return { isValid: true, errorMessage: '', errors }
    }
    
    const ineligibleAttendees = attendees.filter(att => 
      att.eligibilityChecked && att.isEligible === false
    )
    
    if (ineligibleAttendees.length > 0) {
      // Set field-level errors for ineligible attendees
      ineligibleAttendees.forEach(attendee => {
        const eligibilityErrors = createRenewalEligibilityErrors({ id: attendee.id, ...attendee }, epiProps)
        if (Object.keys(eligibilityErrors).length > 0) {
          errors[attendee.id] = eligibilityErrors
        }
      })
      
      const attendeeList = ineligibleAttendees.map(attendee => attendee.email).join(', ')
      const validationMessage = epiProps.attendeesNotEligibleForRenewalSummaryMessage
        ? epiProps.attendeesNotEligibleForRenewalSummaryMessage + " " + attendeeList
        : `These attendees are not eligible for renewal: ` + attendeeList
      
      return {
        isValid: false,
        errorMessage: validationMessage,
        errors
      }
    }
    
    return { isValid: true, errorMessage: '', errors }
  }

  // Validation function. Is called only when the user clicks the 'continue' CTA.
  // Checks in priority order and returns first error.
  // 1. Check for required fields
  // 2. Check that emails are formatted correctly
  // 3. Check for duplicate emails
  // 4. Check renewal eligibility (if signing up for a renewal program)
  const validateAttendeesOnContinue = (attendees: IAttendeeFormData[]): { isValid: boolean; errorMessage: string } => {
    // 1. Check required fields first
    const requiredFieldsValidation = validateRequiredFields(attendees)
    if (!requiredFieldsValidation.isValid) {
      setFormErrors(requiredFieldsValidation.errors)
      return { isValid: false, errorMessage: requiredFieldsValidation.errorMessage }
    }

    // 2. Check that email is formatted correctly
    const emailFormatValidation = validateEmailFormat(attendees)
    if (!emailFormatValidation.isValid) {
      setFormErrors(emailFormatValidation.errors)
      return { isValid: false, errorMessage: emailFormatValidation.errorMessage }
    }

    // 3. Check for duplicate emails
    const duplicateEmailsValidation = validateDuplicateEmails(attendees)
    if (!duplicateEmailsValidation.isValid) {
      setFormErrors(duplicateEmailsValidation.errors)
      return { isValid: false, errorMessage: duplicateEmailsValidation.errorMessage }
    }

    // 4. Check renewal eligibility
    const renewalEligibilityValidation = validateRenewalEligibility(attendees)
    if (!renewalEligibilityValidation.isValid) {
      setFormErrors(renewalEligibilityValidation.errors)
      return { isValid: false, errorMessage: renewalEligibilityValidation.errorMessage }
    }

    // Clear form errors if all validation passes
    setFormErrors({})
    return { isValid: true, errorMessage: '' }
  }

  // Check renewal eligibility for all attendees with valid emails
  const checkAttendeesEligibility = async (attendees: IAttendeeFormData[]): Promise<IAttendeeFormData[]> => {
    const isRenewalProgram = userSelections.userProgramSelection?.isRenewal === true
    const specialtyCode = userSelections.userProgramSelection?.programCode || ''
    
    if (!isRenewalProgram) {
      return attendees
    }

    setIsCheckingEligibility(true)

    try {
      // Check eligibility for all attendees with valid emails
      const eligibilityPromises = attendees
        .filter(att => att.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(att.email))
        .map(async (attendee) => {
          const eligibilityResponse = await checkRenewalEligibility(attendee.email, specialtyCode)
          return {
            ...attendee,
            isEligible: eligibilityResponse.isEligible,
            eligibilityChecked: true
          }
        })
      
      const attendeesWithEligibilityResults = await Promise.all(eligibilityPromises)
      
      // Update attendees with eligibility results
      const updatedAttendees = attendees.map(attendee => {
        const eligibilityResult = attendeesWithEligibilityResults.find(a => a.email === attendee.email)
        return eligibilityResult || attendee
      })
      
      // Update local state with eligibility results
      const updatedLocalAttendees = localAttendees.map(localAttendee => {
        const eligibilityResult = attendeesWithEligibilityResults.find(a => a.email === localAttendee.email)
        return eligibilityResult || localAttendee
      })
      setLocalAttendees(updatedLocalAttendees)
      
      return updatedAttendees
    } catch (error) {
      console.error('Error checking eligibility:', error)
      // Return original attendees if eligibility check fails
      return attendees
    } finally {
      setIsCheckingEligibility(false)
    }
  }

  const handleContinue = async () => {
    // Filter out empty forms (where all fields are empty)
    const filledAttendees = localAttendees.filter(att => 
      att.firstName.trim() || att.lastName.trim() || att.email.trim() || att.title.trim()
    )

    if (filledAttendees.length === 0) {
      setShowValidationMessage(true)
      setValidationMessage(epiProps.attendeesStepValidationMessage)
      return
    }

    // Check renewal eligibility for all attendees (if it's a renewal program)
    const attendeesWithEligibilityStatusUpdated = await checkAttendeesEligibility(filledAttendees)

    // Run validation - checks renewal eligibility, duplicate emails, and email format in order
    const validation = validateAttendeesOnContinue(attendeesWithEligibilityStatusUpdated)
    
    if (!validation.isValid) {
      setShowValidationMessage(true)
      setValidationMessage(validation.errorMessage)
      return
    }

    // All validation passed - clear validation messages, sync state and navigate to next step
    setShowValidationMessage(false)
    setValidationMessage('')
    syncGlobalState(localAttendees)
    dispatch({
      type: 'updateCurrentStep',
      data: StepConstants.ReviewStep
    })
  }

  // Don't render if step should be hidden (international users)
  if (hideAttendeesStep) {
    return null
  }

  if (currentStep === StepConstants.AttendeesStep) {
    return (
      <div>
        <section className="app-section-header">
          <div className="app-section-header-inner">
            <h1>{epiProps.attendeesStepTitle}</h1>
            <p>{epiProps.attendeesStepDescription}</p>
          </div>
        </section>

        <ContentWrapper>
          <MainContent>
            <div className="attendees-step-container">
              {/* Add Attendee Button - only show if under max limit */}
              {localAttendees.length < MAX_ATTENDEES && (
                <button 
                  className="btn-primary btn-add-attendee"
                  onClick={handleAddAttendee}
                >
                  {epiProps.addAttendeeButtonText} +
                </button>
              )}

              {/* Container for all attendee forms */}
              <div className="attendees-forms-container">
                {localAttendees.map((attendee, index) => (
                  <div key={attendee.id} className="attendee-form-wrapper">
                    <AttendeeForm
                      onChange={(updatedAttendee) => handleAttendeeChange(index, updatedAttendee)}
                      onBlur={(updatedAttendee) => handleAttendeeBlur(index, updatedAttendee)}
                      onRemove={() => handleRemoveAttendee(index)}
                      showRemoveButton={localAttendees.length > 1}
                      attendee={attendee}
                      attendeeIndex={attendee.attendeeNumber - 1}
                      errors={formErrors[attendee.id]}
                      isDuplicateEmail={duplicateEmails.has(attendee.email.trim().toLowerCase())}
                      epiProps={epiProps}
                      isRenewalProgram={userSelections.userProgramSelection?.isRenewal === true}
                      specialtyCode={userSelections.userProgramSelection?.programCode}
                    />
                  </div>
                ))}
              </div>
            </div>
          </MainContent>

          <SideBarContent>
            <ProgramSummary
              userSelections={{
                ...userSelections,
                attendees: localAttendees.map(({ id, attendeeNumber, ...att }) => ({
                  firstName: att.firstName,
                  lastName: att.lastName,
                  email: att.email,
                  title: att.title,
                  isEligible: att.isEligible,
                  eligibilityChecked: att.eligibilityChecked
                }))
              }}
              onContinue={handleContinue}
              showValidationMessage={showValidationMessage}
              validationMessage={validationMessage}
              showMaxAttendeesMessage={localAttendees.length >= MAX_ATTENDEES}
              maxAttendeesMessage={epiProps.maxAttendeesMessage}
              isSubmitting={isCheckingEligibility}
            />
          </SideBarContent>
        </ContentWrapper>
      </div>
    )
  }

  return null
}